/**
 * Manual Image Component
 *
 * Displays manual images with fallback support.
 * States: idle -> loading -> loaded or error
 *
 * Features:
 * - Intersection Observer for viewport-based lazy loading
 * - Image exists check before rendering
 * - Loading skeleton and error fallback
 */

import { useState, useEffect, useRef } from 'react';
import { checkImageExists } from '../../utils/markdown';
import type { ManualImageProps, ImageLoadState } from '../../types/manual';
import './ManualImage.css';

export const ManualImage = ({ src, alt, caption, className = '' }: ManualImageProps) => {
  const [loadState, setLoadState] = useState<ImageLoadState>('idle');
  const [showAltText, setShowAltText] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for viewport-based lazy loading
  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    // Skip if already started loading
    if (loadState !== 'idle') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When image enters viewport (with small margin for better UX)
          if (entry.isIntersecting) {
            setLoadState('loading');
            observer.disconnect();
          }
        });
      },
      {
        // Start loading slightly before entering viewport (200px margin)
        rootMargin: '200px',
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [loadState]);

  // Check image existence when state changes to 'loading'
  useEffect(() => {
    if (loadState !== 'loading') return;

    let isMounted = true;

    const checkImage = async () => {
      const exists = await checkImageExists(src);

      if (isMounted) {
        setLoadState(exists ? 'loaded' : 'error');
      }
    };

    checkImage();

    return () => {
      isMounted = false;
    };
  }, [loadState, src]);

  const handleClick = () => {
    if (loadState === 'error') {
      setShowAltText(!showAltText);
    }
  };

  // Idle/Loading state: show skeleton with alt text
  if (loadState === 'idle' || loadState === 'loading') {
    return (
      <figure ref={imgRef} className={`manual-image manual-image--loading ${className}`}>
        <div className="manual-image__skeleton" aria-label={alt}>
          <div className="manual-image__skeleton-animation"></div>
          {alt && <p className="manual-image__alt-text">{alt}</p>}
        </div>
        {caption && <figcaption className="manual-image__caption">{caption}</figcaption>}
      </figure>
    );
  }

  // Error state: show placeholder
  if (loadState === 'error') {
    return (
      <figure
        className={`manual-image manual-image--error ${className}`}
        onClick={handleClick}
        role="img"
        aria-label={alt}
      >
        <div className="manual-image__placeholder">
          <svg
            className="manual-image__placeholder-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
            <path
              d="M6 16L9.5 12L12.5 15L16 11L18 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="manual-image__error-text">圖片載入失敗</p>
          {showAltText && (
            <div className="manual-image__alt-overlay">
              <p className="manual-image__alt-overlay-text">{alt}</p>
            </div>
          )}
        </div>
        {caption && <figcaption className="manual-image__caption">{caption}</figcaption>}
      </figure>
    );
  }

  // Loaded state: show actual image
  return (
    <figure className={`manual-image manual-image--loaded ${className}`}>
      <img src={src} alt={alt} loading="lazy" className="manual-image__img" />
      {caption && <figcaption className="manual-image__caption">{caption}</figcaption>}
    </figure>
  );
};
